import { type ParentComponent, Show, onMount, createEffect, createSignal } from "solid-js";
import { useNavigate, useLocation } from "@solidjs/router";
import { ShellStoreProvider } from "@ui/state/shell";
import { Sidebar } from "@ui/layout/Sidebar";
import { PanelStack } from "@ui/layout/PanelStack";
import { FooterStatusBar } from "@ui/layout/FooterStatusBar";
import { useAuth } from "@core/auth/session";
import { appState } from "@core/state/AppState";

export const AppShell: ParentComponent = (props) => {
  const { state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Sync auth state to AppState
  createEffect(() => {
    appState.setAuthStatus(state.status);
    appState.setCurrentUser(state.user);
    appState.setAuthenticated(state.status === "authorized");
    if (state.error) {
      appState.setAuthError(state.error);
    }
  });

  // Sync current route to AppState
  createEffect(() => {
    appState.setCurrentRoute(location.pathname);
  });

  // Initialize AppState on mount
  onMount(() => {
    appState.initialize();
  });

  // Redirect to login if not authorized
  createEffect(() => {
    if (state.status === "signed-out" || state.status === "unauthorized") {
      navigate("/login", { replace: true });
    }
  });

  // Show loading screen while checking auth
  const isLoading = () => state.status === "loading" || state.status === "authorizing";

  // Mobile sidebar toggle state
  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen());
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <Show
      when={!isLoading()}
      fallback={
        <div class="auth-loading-screen">
          <div class="auth-loading-backdrop"></div>
          <div class="auth-loading-content">
            <div class="auth-loading-logo">
              <img src="/logo.png" alt="HappyTidy" />
            </div>
            <h1 class="auth-loading-title">Signing you in...</h1>
            <p class="auth-loading-subtitle">Checking your administrator access. This just takes a moment.</p>
          </div>
        </div>
      }
    >
      <Show when={state.status === "authorized"}>
        <ShellStoreProvider>
          <div class="app-shell">
            {/* Mobile header with logo, title and hamburger button */}
            <div class="mobile-header">
              <button
                type="button"
                class="mobile-nav-toggle"
                onClick={toggleSidebar}
                aria-label="Toggle navigation"
              >
                <i class={`fa-solid ${isSidebarOpen() ? 'fa-xmark' : 'fa-bars'}`}></i>
              </button>
              <div class="mobile-header-brand">
                <img src="/logo.png" alt="HappyTidy" class="mobile-header-logo" />
                <h1 class="mobile-header-title">HappyTidy</h1>
              </div>
            </div>

            {/* Mobile overlay */}
            <div
              class={`mobile-sidebar-overlay ${isSidebarOpen() ? 'visible' : ''}`}
              onClick={closeSidebar}
            ></div>

            <Sidebar isOpen={isSidebarOpen()} onNavigate={closeSidebar} />
            <main class="main-viewport">{props.children}</main>
            <PanelStack />
            <FooterStatusBar />
          </div>
        </ShellStoreProvider>
      </Show>
    </Show>
  );
};
